import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  DashboardFormsData,
  DashboardInsightsData,
  DashboardOperationsData,
  DashboardOverviewData,
  DashboardQueryParams,
} from '../types/dashboard.types';

/**
 * All three slices share the same branch/date scope, so they also share
 * cache tags: the slice-scoped id allows targeted invalidation, while the
 * branch-scoped and bare tags keep existing invalidations (booking mutations,
 * branch switch) refreshing the whole dashboard.
 */
function dashboardTags(branchCode: string, slice: string) {
  return [
    { type: API_TAGS.Dashboard, id: `${branchCode}-${slice}` },
    { type: API_TAGS.Dashboard, id: branchCode },
    API_TAGS.Dashboard,
  ] as const;
}

/** The API returns `[]` rather than `{}` when no channels have been used. */
function normalizeOperations(
  data: DashboardOperationsData,
): DashboardOperationsData {
  const channels = data.communication.deliveryChannels;
  if (!Array.isArray(channels)) {
    return data;
  }

  return {
    ...data,
    communication: { ...data.communication, deliveryChannels: {} },
  };
}

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardOverview: builder.query<
      DashboardOverviewData,
      DashboardQueryParams
    >({
      query: ({ branchCode, fromDate, toDate }) => ({
        url: `dashboard/branch/${encodeURIComponent(branchCode)}/overview`,
        params: { fromDate, toDate },
      }),
      transformResponse: (
        response:
          Pro20ApiResponse<DashboardOverviewData> | DashboardOverviewData,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode }) => [
        ...dashboardTags(branchCode, 'overview'),
      ],
      keepUnusedDataFor: 300,
    }),

    getDashboardInsights: builder.query<
      DashboardInsightsData,
      DashboardQueryParams
    >({
      query: ({ branchCode, fromDate, toDate }) => ({
        url: `dashboard/branch/${encodeURIComponent(branchCode)}/insights`,
        params: { fromDate, toDate },
      }),
      transformResponse: (
        response:
          Pro20ApiResponse<DashboardInsightsData> | DashboardInsightsData,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode }) => [
        ...dashboardTags(branchCode, 'insights'),
      ],
      keepUnusedDataFor: 300,
    }),

    getDashboardOperations: builder.query<
      DashboardOperationsData,
      DashboardQueryParams
    >({
      query: ({ branchCode, fromDate, toDate }) => ({
        url: `dashboard/branch/${encodeURIComponent(branchCode)}/operations`,
        params: { fromDate, toDate },
      }),
      transformResponse: (
        response:
          Pro20ApiResponse<DashboardOperationsData> | DashboardOperationsData,
      ) => normalizeOperations(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode }) => [
        ...dashboardTags(branchCode, 'operations'),
      ],
      keepUnusedDataFor: 300,
    }),

    getDashboardForms: builder.query<DashboardFormsData, DashboardQueryParams>({
      query: ({ branchCode, fromDate, toDate }) => ({
        url: `dashboard/branch/${encodeURIComponent(branchCode)}/forms`,
        params: { fromDate, toDate },
      }),
      transformResponse: (
        response: Pro20ApiResponse<DashboardFormsData> | DashboardFormsData,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode }) => [
        ...dashboardTags(branchCode, 'forms'),
      ],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetDashboardOverviewQuery,
  useGetDashboardInsightsQuery,
  useGetDashboardOperationsQuery,
  useGetDashboardFormsQuery,
} = dashboardApi;
